import { useEffect, useRef } from 'react'

type UseScrollHighlightOptions = {
    highlightId: string | null | undefined
    onClear: () => void
    delay?: number
    scrollOptions?: ScrollIntoViewOptions
    maxAttempts?: number
    attemptInterval?: number
}

export const useScrollHighlight = ({
    highlightId,
    onClear,
    delay = 1500,
    scrollOptions,
    maxAttempts = 20,
    attemptInterval = 250,
}: UseScrollHighlightOptions) => {
    const onClearRef = useRef(onClear)
    onClearRef.current = onClear

    useEffect(() => {
        if (!highlightId) return

        let attempt = 0
        let scrollTimer: ReturnType<typeof setTimeout> | null = null
        let clearTimer: ReturnType<typeof setTimeout> | null = null

        const tryScroll = () => {
            try {
                const element = document.querySelector(`[data-highlight-id="${highlightId}"]`)
                if (element) {
                    element.scrollIntoView(scrollOptions ?? { behavior: 'smooth', block: 'center', inline: 'nearest' })
                    clearTimer = setTimeout(() => onClearRef.current(), delay)
                    return
                }
            } catch {}

            attempt++
            if (attempt < maxAttempts) {
                scrollTimer = setTimeout(tryScroll, attemptInterval)
            } else {
                clearTimer = setTimeout(() => onClearRef.current(), delay)
            }
        }

        tryScroll()

        return () => {
            if (scrollTimer) clearTimeout(scrollTimer)
            if (clearTimer) clearTimeout(clearTimer)
        }
    }, [highlightId, delay, scrollOptions, maxAttempts, attemptInterval])
}
